import { z } from "zod";

import { compareDecimalStrings, PERCENTAGE_INPUT_PATTERN, trimTrailingZeros } from "@/shared/utils/decimal";

import type { CreatePaymentTaxPayload, PaymentTax } from "../../types/paymentTaxes.types";
import { toIsoInstant } from "../fees/corridorPriceFormSchema";

export const TAX_CODE_PATTERN = /^[A-Z0-9]{2,8}$/;
/** Mirrors the backend CHECK: on the gross a higher rate leaves no fixed-net price. */
export const MAX_GROSS_AMOUNT_RATE = "10";

export const TAX_DIRECTION_CHOICES = ["BOTH", "PAYIN", "PAYOUT"] as const;
export const ANY_RAIL = "ANY";

export interface TaxFormMessages {
  code: string;
  name: string;
  rate: string;
  rateGross: string;
  validFrom: string;
  reason: string;
  acknowledge: string;
}

export function buildTaxFormSchema(messages: TaxFormMessages) {
  return z
    .object({
      country: z.string().length(3),
      code: z.string().trim().toUpperCase().regex(TAX_CODE_PATTERN, messages.code),
      name: z.string().trim().min(1, messages.name).max(64, messages.name),
      base: z.enum(["COMMISSION", "GROSS_AMOUNT"]),
      bearer: z.enum(["CLIENT", "COLOCA"]),
      rate: z.string().trim().regex(PERCENTAGE_INPUT_PATTERN, messages.rate),
      direction: z.enum(TAX_DIRECTION_CHOICES),
      rail: z.string(),
      effectiveNow: z.boolean(),
      validFromDate: z.string(),
      validFromTime: z.string(),
      reason: z.string().max(500),
      requiresAck: z.boolean(),
      acknowledged: z.boolean(),
    })
    .superRefine((values, ctx) => {
      if (
        values.base === "GROSS_AMOUNT" &&
        PERCENTAGE_INPUT_PATTERN.test(values.rate) &&
        compareDecimalStrings(values.rate, MAX_GROSS_AMOUNT_RATE) > 0
      ) {
        ctx.addIssue({ code: "custom", path: ["rate"], message: messages.rateGross });
      }
      if (!values.effectiveNow && !values.validFromDate) {
        ctx.addIssue({ code: "custom", path: ["validFromDate"], message: messages.validFrom });
      }
      if (values.requiresAck) {
        if (!values.reason.trim()) {
          ctx.addIssue({ code: "custom", path: ["reason"], message: messages.reason });
        }
        if (!values.acknowledged) {
          ctx.addIssue({ code: "custom", path: ["acknowledged"], message: messages.acknowledge });
        }
      }
    });
}

export type TaxFormValues = z.input<ReturnType<typeof buildTaxFormSchema>>;

/** The fields checked before the review step opens. */
export const TAX_STEP_FIELDS = [
  "country",
  "code",
  "name",
  "base",
  "bearer",
  "rate",
  "direction",
  "rail",
  "validFromDate",
] as const satisfies readonly (keyof TaxFormValues)[];

/** A new version starts from the one in force at that scope; a new tax from a blank IVA-shaped form. */
export function seedTaxForm(
  source: PaymentTax | null,
  defaults: { country: string; requiresAck: boolean },
): TaxFormValues {
  return {
    country: source?.country ?? defaults.country,
    code: source?.code ?? "",
    name: source?.name ?? "",
    base: source?.base ?? "COMMISSION",
    bearer: source?.bearer ?? "CLIENT",
    rate: source ? trimTrailingZeros(source.rate) : "",
    direction: source?.direction ?? "BOTH",
    rail: source?.rail ?? ANY_RAIL,
    effectiveNow: true,
    validFromDate: "",
    validFromTime: "00:00",
    reason: "",
    requiresAck: defaults.requiresAck,
    acknowledged: false,
  };
}

export function toCreateTaxPayload(
  values: TaxFormValues,
  organizationId: string | null,
): CreatePaymentTaxPayload {
  const validFrom = values.effectiveNow ? null : toIsoInstant(values.validFromDate, values.validFromTime);
  const reason = values.reason.trim();
  return {
    code: values.code.trim().toUpperCase(),
    name: values.name.trim(),
    country: values.country,
    base: values.base,
    bearer: values.bearer,
    rate: values.rate.trim(),
    ...(organizationId ? { organization_id: organizationId } : {}),
    ...(values.rail !== ANY_RAIL ? { rail: values.rail as CreatePaymentTaxPayload["rail"] } : {}),
    ...(values.direction !== "BOTH" ? { direction: values.direction } : {}),
    ...(validFrom ? { valid_from: validFrom } : {}),
    ...(reason ? { change_reason: reason } : {}),
  };
}
